import React, { useState } from 'react';
import {
  buildQzTestPageHtml,
  getDefaultQzSettings,
  printHtmlWithQz,
  QZ_PRINT_PROFILES,
} from '../../services/qzPrintService';
import './QzPrinterSettingsPanel.css';

const findPrinter = (details, name) =>
  (details || []).find((printer) => printer.name === name) || null;

export default function QzPrinterSettingsPanel({
  profileKeys = [],
  printerDetails = [],
  settings = {},
  onChange,
}) {
  const [testStatus, setTestStatus] = useState({});

  const update = (profileKey, patch) => {
    onChange?.({
      ...settings,
      [profileKey]: {
        ...getDefaultQzSettings(profileKey),
        ...(settings[profileKey] || {}),
        ...patch,
      },
    });
  };

  return (
    <div className="qz-printer-settings">
      {profileKeys.map((profileKey) => {
        const profile = QZ_PRINT_PROFILES[profileKey];
        if (!profile) return null;
        const value = { ...getDefaultQzSettings(profileKey), ...(settings[profileKey] || {}) };
        const printer = findPrinter(printerDetails, value.printer);
        const trays = printer?.trays || [];
        const sizes = printer?.sizes || [];
        const densities = printer?.densities || [];
        return (
          <details key={profileKey} className="qz-printer-profile">
            <summary>
              <span>
                <b>{profile.label}</b>
                <small>
                  {printer?.name || 'Select printer'} ·{' '}
                  {value.printerTray || 'Printer default tray'}
                </small>
              </span>
              <span className="qz-printer-profile__edit">Settings</span>
            </summary>
            <div className="qz-printer-profile__body">
              <label>
                Printer
                <select
                  value={printer?.name || ''}
                  onChange={(event) => {
                    const nextPrinter = findPrinter(printerDetails, event.target.value);
                    const defaultTray = nextPrinter?.trays?.includes(profile.printerTray)
                      ? profile.printerTray
                      : nextPrinter?.trays?.[0] || '';
                    const defaultSize =
                      nextPrinter?.sizes?.find((size) => size.name === profile.paperSizeName) ||
                      nextPrinter?.sizes?.[0];
                    update(profileKey, {
                      printer: event.target.value,
                      printerTray: defaultTray,
                      paperSizeName: defaultSize?.name || profile.paperSizeName,
                      size: defaultSize?.size || { ...profile.size },
                      density: nextPrinter?.densities?.includes(profile.density)
                        ? profile.density
                        : nextPrinter?.densities?.[0] || profile.density,
                    });
                  }}
                >
                  {!printer && value.printer && (
                    <option value={value.printer}>{value.printer} (unavailable)</option>
                  )}
                  {(printerDetails || []).map((item) => (
                    <option key={item.name} value={item.name}>
                      {item.name}
                    </option>
                  ))}
                </select>
              </label>

              <label>
                Paper source
                <select
                  value={value.printerTray}
                  onChange={(event) =>
                    update(profileKey, { printerTray: event.target.value })
                  }
                >
                  {!trays.length && <option value={value.printerTray}>{value.printerTray}</option>}
                  {trays.map((tray) => (
                    <option key={tray} value={tray}>{tray}</option>
                  ))}
                </select>
              </label>

              <label>
                Paper size
                <select
                  value={value.paperSizeName}
                  onChange={(event) => {
                    const selected = sizes.find((size) => size.name === event.target.value);
                    update(profileKey, {
                      paperSizeName: event.target.value,
                      size: selected?.size || value.size,
                    });
                  }}
                >
                  {!sizes.length && (
                    <option value={value.paperSizeName}>{value.paperSizeName}</option>
                  )}
                  {sizes.map((size) => (
                    <option key={size.name} value={size.name}>{size.name}</option>
                  ))}
                </select>
              </label>

              <label>
                Orientation
                <select
                  value={value.orientation}
                  onChange={(event) => update(profileKey, { orientation: event.target.value })}
                >
                  <option value="portrait">Portrait</option>
                  <option value="landscape">Landscape</option>
                  <option value="reverse-landscape">Reverse landscape</option>
                </select>
              </label>

              <label>
                Rotation
                <select
                  value={profileKey === 'giftCard' ? 180 : value.rotation}
                  disabled={profileKey === 'giftCard'}
                  onChange={(event) => update(profileKey, { rotation: Number(event.target.value) })}
                >
                  <option value="0">0°</option>
                  <option value="90">90°</option>
                  <option value="180">180°</option>
                  <option value="270">270°</option>
                </select>
                {profileKey === 'giftCard' ? (
                  <small>Locked at 180° for the Multipurpose tray feed.</small>
                ) : null}
              </label>

              <label>
                Density
                <select
                  value={value.density}
                  onChange={(event) =>
                    update(profileKey, {
                      density:
                        event.target.value === 'best'
                          ? 'best'
                          : Number(event.target.value),
                    })
                  }
                >
                  <option value="best">Best supported</option>
                  {!densities.includes(Number(value.density)) &&
                    Number(value.density) > 0 && (
                      <option value={value.density}>{value.density} dpi</option>
                    )}
                  {densities.map((density) => (
                    <option key={density} value={density}>{density} dpi</option>
                  ))}
                </select>
              </label>

              <label>
                Colour
                <select
                  value={value.colorType}
                  onChange={(event) => update(profileKey, { colorType: event.target.value })}
                >
                  <option value="default">Printer default</option>
                  <option value="color">Colour</option>
                  <option value="grayscale">Greyscale</option>
                  <option value="blackwhite">Black and white</option>
                </select>
              </label>

              <label>
                Sides
                <select
                  value={value.duplex}
                  onChange={(event) => update(profileKey, { duplex: event.target.value })}
                >
                  <option value="one-sided">One-sided</option>
                  <option value="long-edge">Double-sided, long edge</option>
                  <option value="short-edge">Double-sided, short edge</option>
                  <option value="tumble">Double-sided, tumble</option>
                </select>
              </label>

              <label>
                Driver margin (mm)
                <input
                  type="number"
                  min="0"
                  max="30"
                  step="0.5"
                  value={value.margins}
                  onChange={(event) =>
                    update(profileKey, {
                      margins: Math.max(0, Number(event.target.value) || 0),
                    })
                  }
                />
              </label>

              <label>
                Width (mm)
                <input
                  type="number"
                  min="20"
                  max="500"
                  step="0.1"
                  value={value.size?.width || ''}
                  onChange={(event) =>
                    update(profileKey, {
                      size: { ...value.size, width: Number(event.target.value) || 0 },
                    })
                  }
                />
              </label>

              <label>
                Height (mm)
                <input
                  type="number"
                  min="20"
                  max="500"
                  step="0.1"
                  value={value.size?.height || ''}
                  onChange={(event) =>
                    update(profileKey, {
                      size: { ...value.size, height: Number(event.target.value) || 0 },
                    })
                  }
                />
              </label>

              <p className="qz-printer-profile__meta">
                Driver: {printer?.driver || 'Not reported'}
                {printer?.connection ? ` · ${printer.connection}` : ''}
                {' · '}Content always fitted to selected paper
                {' · '}
                {profileKey === 'shippingLabel'
                  ? 'Carrier PDFs remain vector'
                  : profileKey === 'go4Outside'
                    ? 'GO4 outside HTML on GoDEX stock (not carrier PDF)'
                    : 'HTML rasterisation always enabled'}
              </p>
              {/Microsoft IPP Class Driver/i.test(printer?.driver || '') &&
                /Brother MFC-J6955DW/i.test(printer?.name || '') && (
                  <p className="qz-printer-profile__warning">
                    This printer is using Microsoft’s generic 300 DPI IPP driver.
                    Install Brother’s full MFC-J6955DW driver for sharper output and
                    complete tray controls.
                  </p>
                )}
              {!printer && (
                <p className="qz-printer-profile__warning">
                  The saved printer is unavailable. Select an available printer or use
                  browser printing.
                </p>
              )}
              {printer && printer.capabilitiesAvailable === false && (
                <p className="qz-printer-profile__warning">
                  QZ could not read detailed capabilities. The configured driver
                  defaults may be used for tray and media.
                </p>
              )}
              {printer?.capabilitiesAvailable !== false &&
                value.printerTray &&
                !trays.includes(value.printerTray) && (
                  <p className="qz-printer-profile__warning">
                    The selected paper source was not reported by this printer.
                  </p>
                )}
              {printer?.capabilitiesAvailable !== false &&
                value.paperSizeName &&
                !sizes.some((size) => size.name === value.paperSizeName) && (
                  <p className="qz-printer-profile__warning">
                    The selected paper size was not reported by this printer.
                  </p>
                )}
              <button
                type="button"
                className="toolkit-btn toolkit-btn--ghost"
                onClick={() => update(profileKey, getDefaultQzSettings(profileKey))}
              >
                Restore London Smoke &amp; Cure default
              </button>
              <button
                type="button"
                className="toolkit-btn toolkit-btn--secondary"
                disabled={testStatus[profileKey]?.busy}
                onClick={async () => {
                  setTestStatus((previous) => ({
                    ...previous,
                    [profileKey]: { busy: true, message: 'Sending test page…' },
                  }));
                  try {
                    const result = await printHtmlWithQz(
                      profileKey,
                      buildQzTestPageHtml(profileKey, value),
                      value,
                    );
                    setTestStatus((previous) => ({
                      ...previous,
                      [profileKey]: {
                        busy: false,
                        message: `Test submitted to ${result.printer} · ${
                          result.tray || 'default tray'
                        }. Check the physical test page before using this profile.`,
                      },
                    }));
                  } catch (error) {
                    setTestStatus((previous) => ({
                      ...previous,
                      [profileKey]: {
                        busy: false,
                        error: true,
                        message: error?.message || 'QZ test print failed.',
                      },
                    }));
                  }
                }}
              >
                {testStatus[profileKey]?.busy ? 'Sending test…' : 'Print test page'}
              </button>
              {testStatus[profileKey]?.message && (
                <p
                  className={`qz-printer-profile__test-status${
                    testStatus[profileKey]?.error ? ' is-error' : ''
                  }`}
                >
                  {testStatus[profileKey].message}
                </p>
              )}
            </div>
          </details>
        );
      })}
    </div>
  );
}
